import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Mailbox,
    Gavel,
    AlertTriangle,
    Trophy,
    HelpCircle,
    FileQuestion,
    Plus,
    Pencil,
    Trash2,
    Search,
    Paperclip,
    Download,
    X,
    Loader2,
    Save,
    Calendar,
    Building2,
    Link2,
    Folder,
    FolderOpen,
    ChevronDown,
    ChevronRight,
    CalendarClock,
    Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    listCommunications,
    createCommunication,
    updateCommunication,
    deleteCommunication,
    uploadCommunicationAttachment,
    communicationAttachmentUrl,
    deleteCommunicationAttachment,
    extractCommunicationDeadline,
    listTenders,
} from "@/lib/api";

const KINDS = [
    {
        key: "mesa",
        label: "Mesa de contratación",
        icon: Gavel,
        cls: "bg-violet-50 text-violet-700 border-violet-200",
        gradient: "from-violet-500 to-fuchsia-600",
    },
    {
        key: "requerimiento",
        label: "Requerimiento",
        icon: AlertTriangle,
        cls: "bg-amber-50 text-amber-700 border-amber-200",
        gradient: "from-amber-500 to-orange-600",
    },
    {
        key: "adjudicacion",
        label: "Adjudicación",
        icon: Trophy,
        cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
        gradient: "from-emerald-500 to-teal-600",
    },
    {
        key: "aclaracion",
        label: "Aclaración",
        icon: HelpCircle,
        cls: "bg-blue-50 text-blue-700 border-blue-200",
        gradient: "from-blue-500 to-indigo-600",
    },
    {
        key: "otra",
        label: "Otra",
        icon: FileQuestion,
        cls: "bg-slate-50 text-slate-700 border-slate-200",
        gradient: "from-slate-500 to-slate-700",
    },
];

const kindMeta = (k) => KINDS.find((x) => x.key === k) || KINDS[KINDS.length - 1];

export default function Comunicaciones() {
    const [items, setItems] = useState([]);
    const [tenders, setTenders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [kindFilter, setKindFilter] = useState("");
    const [editing, setEditing] = useState(null); // {id?, …} or 'new' (an object means open)
    const [opened, setOpened] = useState(null); // viewing one

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [d, t] = await Promise.all([listCommunications(), listTenders()]);
            setItems(d.communications || []);
            setTenders(t || []);
        } catch {
            toast.error("No se pudieron cargar las comunicaciones");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // While any communication is still 'pending' AI extraction, poll silently
    // every 6s so the user sees the deadline pop in without refreshing.
    useEffect(() => {
        const hasPending = items.some(
            (i) => (i.extracted_deadline_confidence || "") === "pending"
        );
        if (!hasPending) return;
        const t = setInterval(() => {
            listCommunications()
                .then((d) => setItems(d.communications || []))
                .catch(() => {});
        }, 6000);
        return () => clearInterval(t);
    }, [items]);

    const filtered = useMemo(() => {
        let out = items;
        if (kindFilter) out = out.filter((i) => i.kind === kindFilter);
        const q = query.trim().toLowerCase();
        if (q) {
            out = out.filter(
                (i) =>
                    (i.title || "").toLowerCase().includes(q) ||
                    (i.sender || "").toLowerCase().includes(q) ||
                    (i.body || "").toLowerCase().includes(q) ||
                    (i.tender_title || "").toLowerCase().includes(q)
            );
        }
        return out;
    }, [items, query, kindFilter]);

    const kindCounts = useMemo(() => {
        const c = {};
        for (const i of items) c[i.kind] = (c[i.kind] || 0) + 1;
        return c;
    }, [items]);

    // Group communications by tender (one auto-folder per tender that has at least
    // one communication, plus a "Sin licitación" bucket for null tender_id).
    const groups = useMemo(() => {
        const m = new Map();
        for (const it of filtered) {
            const key = it.tender_id || "__none__";
            const title =
                it.tender_id
                    ? it.tender_title || "Licitación"
                    : "Sin licitación vinculada";
            if (!m.has(key)) m.set(key, { key, title, items: [] });
            m.get(key).items.push(it);
        }
        // Sort by most-recent activity within group, then put __none__ last
        const arr = Array.from(m.values()).sort((a, b) => {
            if (a.key === "__none__") return 1;
            if (b.key === "__none__") return -1;
            return a.title.localeCompare(b.title, "es");
        });
        // Sort items inside each group by date desc
        arr.forEach((g) => {
            g.items.sort((x, y) => (y.date || "").localeCompare(x.date || ""));
        });
        return arr;
    }, [filtered]);

    const [collapsedGroups, setCollapsedGroups] = useState({});
    const toggleGroup = (key) =>
        setCollapsedGroups((g) => ({ ...g, [key]: !g[key] }));

    const handleDelete = async (c) => {
        if (!window.confirm(`¿Borrar "${c.title}"?`)) return;
        try {
            await deleteCommunication(c.id);
            toast.success("Eliminada");
            await load();
            if (opened?.id === c.id) setOpened(null);
        } catch {
            toast.error("Error al eliminar");
        }
    };

    return (
        <Layout>
            <div className="max-w-[1500px] mx-auto p-6 md:p-8" data-testid="comunicaciones-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-violet-600 mb-2 flex items-center gap-1.5">
                            <Mailbox size={12} />
                            Mesa de contratación · Requerimientos · Adjudicaciones
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black text-ink-950 leading-[1.05]">
                            Comunicaciones
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium max-w-2xl">
                            Centraliza todo lo que recibes del órgano de contratación: actas
                            de la mesa, requerimientos de documentación, resoluciones de
                            adjudicación o aclaraciones.
                        </p>
                    </div>
                    <Button
                        onClick={() => setEditing({})}
                        className="rounded-full h-11 px-5 bg-ink-950 hover:bg-ink-900 text-white font-bold gap-1.5 shadow-pop"
                        data-testid="comunicaciones-new"
                    >
                        <Plus size={14} />
                        Nueva comunicación
                    </Button>
                </div>

                {/* Kind filter pills */}
                <div className="flex flex-wrap gap-2 mb-5">
                    <button
                        onClick={() => setKindFilter("")}
                        className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-bold transition-all border ${kindFilter === "" ? "bg-ink-950 text-white border-ink-950" : "bg-white text-ink-600 border-ink-200 hover:border-ink-400"}`}
                        data-testid="comunicaciones-filter-all"
                    >
                        Todas
                        <span className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full bg-white/20">
                            {items.length}
                        </span>
                    </button>
                    {KINDS.map((k) => {
                        const Icon = k.icon;
                        const active = kindFilter === k.key;
                        const n = kindCounts[k.key] || 0;
                        return (
                            <button
                                key={k.key}
                                onClick={() => setKindFilter(active ? "" : k.key)}
                                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-bold transition-all border ${active ? `${k.cls} ring-2 ring-offset-1 ring-current` : "bg-white text-ink-500 border-ink-200 hover:border-ink-400"}`}
                                data-testid={`comunicaciones-filter-${k.key}`}
                            >
                                <Icon size={12} />
                                {k.label}
                                {n > 0 && (
                                    <span className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full bg-white/40">
                                        {n}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    <div className="ml-auto relative">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Buscar comunicación…"
                            className="pl-9 rounded-full h-9 w-64 border-ink-100"
                        />
                    </div>
                </div>

                {/* List */}
                {loading ? (
                    <div className="bg-white border border-ink-100 rounded-2xl p-16 text-center">
                        <Loader2 className="mx-auto text-brand-600 animate-spin mb-3" size={28} />
                        <div className="text-sm font-bold text-ink-500">Cargando…</div>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white border border-ink-100 border-dashed rounded-2xl p-16 text-center">
                        <Mailbox className="mx-auto text-ink-300 mb-3" size={42} />
                        <div className="font-display text-2xl font-black text-ink-950 mb-1">
                            Sin comunicaciones
                        </div>
                        <div className="text-sm text-ink-500 font-medium mb-6">
                            Cuando la mesa de contratación te envíe una notificación, regístrala aquí
                            para tenerla siempre localizada y vinculada a su licitación.
                        </div>
                        <Button
                            onClick={() => setEditing({})}
                            className="rounded-full h-10 px-5 bg-ink-950 text-white font-bold gap-1.5"
                        >
                            <Plus size={14} /> Crear la primera
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-5" data-testid="comunicaciones-groups">
                        {groups.map((g) => {
                            const collapsed = collapsedGroups[g.key];
                            const isUnlinked = g.key === "__none__";
                            return (
                                <div key={g.key} data-testid={`comunicaciones-group-${g.key}`}>
                                    <button
                                        type="button"
                                        onClick={() => toggleGroup(g.key)}
                                        className={`w-full flex items-center gap-3 p-3 mb-2 rounded-2xl border-2 transition-all ${isUnlinked ? "bg-slate-50 border-slate-200 hover:bg-slate-100" : "bg-gradient-to-r from-brand-50 to-violet-50 border-brand-200 hover:from-brand-100 hover:to-violet-100"}`}
                                    >
                                        <div className={`shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl ${isUnlinked ? "bg-slate-300 text-white" : "bg-gradient-to-br from-brand-500 to-violet-600 text-white"} shadow-pop`}>
                                            {collapsed ? (
                                                <Folder size={18} strokeWidth={2.5} />
                                            ) : (
                                                <FolderOpen size={18} strokeWidth={2.5} />
                                            )}
                                        </div>
                                        <div className="flex-1 text-left min-w-0">
                                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-400 mb-0.5">
                                                {isUnlinked ? "Sin vincular" : "Licitación"}
                                            </div>
                                            <div className="font-display text-base font-black text-ink-950 leading-tight truncate">
                                                {g.title}
                                            </div>
                                        </div>
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white text-ink-700 border border-ink-200 shrink-0">
                                            {g.items.length} comunicación{g.items.length === 1 ? "" : "es"}
                                        </span>
                                        {collapsed ? (
                                            <ChevronRight size={16} className="text-ink-400 shrink-0" />
                                        ) : (
                                            <ChevronDown size={16} className="text-ink-400 shrink-0" />
                                        )}
                                    </button>
                                    {!collapsed && (
                                        <div className="grid lg:grid-cols-2 gap-3 pl-3 border-l-2 border-ink-100 ml-5">
                                            {g.items.map((c, idx) => {
                                                const meta = kindMeta(c.kind);
                                                const Icon = meta.icon;
                                                const hasDeadline = c.extracted_deadline_date;
                                                const deadlinePending = c.extracted_deadline_confidence === "pending";
                                                return (
                                                    <motion.div
                                                        key={c.id}
                                                        initial={{ opacity: 0, y: 6 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: idx * 0.02 }}
                                                        onClick={() => setOpened(c)}
                                                        className="group bg-white border border-ink-100 rounded-2xl p-4 hover:border-violet-300 hover:shadow-pop transition-all cursor-pointer"
                                                        data-testid={`comunicaciones-card-${c.id}`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className={`shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br ${meta.gradient} text-white shadow-pop`}>
                                                                <Icon size={20} strokeWidth={2.5} />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-start justify-between gap-2 mb-1">
                                                                    <h3 className="font-display text-base font-black text-ink-950 leading-tight line-clamp-1">
                                                                        {c.title}
                                                                    </h3>
                                                                    <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${meta.cls}`}>
                                                                        {meta.label}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-3 text-[11px] text-ink-500 font-medium mb-1.5 flex-wrap">
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <Calendar size={11} />
                                                                        {c.date || "—"}
                                                                    </span>
                                                                    {c.sender && (
                                                                        <span className="inline-flex items-center gap-1 truncate">
                                                                            <Building2 size={11} />
                                                                            <span className="truncate">{c.sender}</span>
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {hasDeadline && (
                                                                    <div className="inline-flex items-center gap-1.5 text-[11px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-full mb-1.5">
                                                                        <CalendarClock size={11} />
                                                                        {c.extracted_deadline_label || "Plazo"}: {c.extracted_deadline_date}
                                                                    </div>
                                                                )}
                                                                {deadlinePending && (
                                                                    <div className="inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full mb-1.5">
                                                                        <Loader2 size={11} className="animate-spin" />
                                                                        IA detectando plazo…
                                                                    </div>
                                                                )}
                                                                {c.body && (
                                                                    <p className="text-sm text-ink-600 leading-snug line-clamp-2">
                                                                        {c.body}
                                                                    </p>
                                                                )}
                                                                {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                                                                    <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-ink-500">
                                                                        <Paperclip size={11} />
                                                                        {c.attachments.length} adjunto
                                                                        {c.attachments.length === 1 ? "" : "s"}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setEditing(c);
                                                                    }}
                                                                    className="p-1.5 rounded-md hover:bg-blue-50 text-blue-600"
                                                                    title="Editar"
                                                                >
                                                                    <Pencil size={13} />
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDelete(c);
                                                                    }}
                                                                    className="p-1.5 rounded-md hover:bg-red-50 text-red-500"
                                                                    title="Eliminar"
                                                                    data-testid={`comunicaciones-del-${c.id}`}
                                                                >
                                                                    <Trash2 size={13} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Editor */}
                <AnimatePresence>
                    {editing && (
                        <CommunicationEditor
                            initial={editing}
                            tenders={tenders}
                            onClose={() => setEditing(null)}
                            onSaved={async () => {
                                setEditing(null);
                                await load();
                            }}
                        />
                    )}
                </AnimatePresence>

                {/* Detail viewer */}
                <AnimatePresence>
                    {opened && (
                        <CommunicationViewer
                            communication={opened}
                            onClose={() => setOpened(null)}
                            onEdit={() => {
                                setEditing(opened);
                                setOpened(null);
                            }}
                            onRefresh={async () => {
                                await load();
                                // Reload current opened from fresh items
                                const fresh = items.find((x) => x.id === opened.id);
                                if (fresh) setOpened(fresh);
                            }}
                        />
                    )}
                </AnimatePresence>
            </div>
        </Layout>
    );
}

function CommunicationEditor({ initial, tenders, onClose, onSaved }) {
    const isEdit = Boolean(initial?.id);
    const [title, setTitle] = useState(initial?.title || "");
    const [kind, setKind] = useState(initial?.kind || "mesa");
    const [date, setDate] = useState(initial?.date || new Date().toISOString().slice(0, 10));
    const [sender, setSender] = useState(initial?.sender || "");
    const [body, setBody] = useState(initial?.body || "");
    const [tenderId, setTenderId] = useState(initial?.tender_id || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!title.trim()) {
            toast.error("Pon un título");
            return;
        }
        setSaving(true);
        const payload = {
            title: title.trim(),
            kind,
            date,
            sender: sender.trim(),
            body,
            tender_id: tenderId || null,
        };
        try {
            if (isEdit) {
                await updateCommunication(initial.id, payload);
                toast.success("Comunicación actualizada");
            } else {
                await createCommunication(payload);
                toast.success("Comunicación creada");
            }
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                data-testid="comunicaciones-editor"
            >
                <div className="flex items-center justify-between p-4 border-b border-ink-100">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-600 flex items-center gap-1.5">
                        <Mailbox size={12} />
                        {isEdit ? "Editar comunicación" : "Nueva comunicación"}
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-ink-50">
                        <X size={18} />
                    </button>
                </div>
                <div className="p-5 space-y-3 flex-1 overflow-auto">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                            Título
                        </Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Ej. Convocatoria mesa apertura sobre B"
                            className="font-display text-lg font-black"
                            data-testid="comunicaciones-editor-title"
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                                Tipo
                            </Label>
                            <select
                                value={kind}
                                onChange={(e) => setKind(e.target.value)}
                                className="w-full h-10 px-3 rounded-md border border-ink-200 text-sm font-bold bg-white"
                                data-testid="comunicaciones-editor-kind"
                            >
                                {KINDS.map((k) => (
                                    <option key={k.key} value={k.key}>
                                        {k.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                                Fecha
                            </Label>
                            <Input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                            Remitente
                        </Label>
                        <Input
                            value={sender}
                            onChange={(e) => setSender(e.target.value)}
                            placeholder="Ej. Mesa de Contratación · Ayuntamiento de Madrid"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                            Licitación relacionada (opcional)
                        </Label>
                        <select
                            value={tenderId}
                            onChange={(e) => setTenderId(e.target.value)}
                            className="w-full h-10 px-3 rounded-md border border-ink-200 text-sm bg-white"
                            data-testid="comunicaciones-editor-tender"
                        >
                            <option value="">— Sin vincular —</option>
                            {tenders.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.title}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                            Contenido
                        </Label>
                        <Textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Pega aquí el texto de la comunicación o tus notas…"
                            rows={9}
                            className="font-mono text-sm resize-none"
                            data-testid="comunicaciones-editor-body"
                        />
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 p-4 border-t border-ink-100">
                    <Button onClick={onClose} variant="outline" className="rounded-full h-10 px-5 font-bold">
                        Cancelar
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-full h-10 px-5 bg-ink-950 hover:bg-ink-900 text-white font-bold gap-1.5"
                        data-testid="comunicaciones-editor-save"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {isEdit ? "Guardar cambios" : "Crear"}
                    </Button>
                </div>
            </motion.div>
        </motion.div>
    );
}

function CommunicationViewer({ communication, onClose, onEdit, onRefresh }) {
    const meta = kindMeta(communication.kind);
    const Icon = meta.icon;
    const [uploading, setUploading] = useState(false);
    const fileInput = useRef(null);

    const handleAttach = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            await uploadCommunicationAttachment(communication.id, file);
            toast.success("Adjunto subido");
            await onRefresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al subir");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteAtt = async (aid) => {
        if (!window.confirm("¿Borrar adjunto?")) return;
        try {
            await deleteCommunicationAttachment(communication.id, aid);
            toast.success("Adjunto eliminado");
            await onRefresh();
        } catch {
            toast.error("Error");
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col"
                data-testid="comunicaciones-viewer"
            >
                <div className="flex items-start gap-3 p-5 border-b border-ink-100">
                    <div className={`shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${meta.gradient} text-white shadow-pop`}>
                        <Icon size={22} strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className={`text-[10px] font-bold uppercase tracking-[0.2em] inline-block px-2 py-0.5 rounded-full border mb-1 ${meta.cls}`}>
                            {meta.label}
                        </div>
                        <h2 className="font-display text-2xl font-black text-ink-950 leading-tight">
                            {communication.title}
                        </h2>
                        <div className="flex items-center gap-3 text-xs text-ink-500 font-medium mt-1.5">
                            <span className="inline-flex items-center gap-1">
                                <Calendar size={12} />
                                {communication.date}
                            </span>
                            {communication.sender && (
                                <span className="inline-flex items-center gap-1">
                                    <Building2 size={12} />
                                    {communication.sender}
                                </span>
                            )}
                            {communication.tender_title && (
                                <span className="inline-flex items-center gap-1 text-brand-700">
                                    <Link2 size={12} />
                                    {communication.tender_title}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-1">
                        <Button
                            onClick={onEdit}
                            variant="outline"
                            className="rounded-full h-9 px-3 border-ink-200 font-bold gap-1.5"
                        >
                            <Pencil size={13} />
                            Editar
                        </Button>
                        <button onClick={onClose} className="p-2 rounded-full hover:bg-ink-50">
                            <X size={18} />
                        </button>
                    </div>
                </div>
                <div className="p-5 flex-1 overflow-auto">
                    {/* AI-extracted action deadline */}
                    {communication.extracted_deadline_date ? (
                        <div className="mb-5 p-4 rounded-xl bg-gradient-to-br from-red-50 to-orange-50 border border-red-200">
                            <div className="flex items-start gap-3">
                                <div className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 text-white shadow-pop">
                                    <CalendarClock size={20} strokeWidth={2.5} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-700 mb-1">
                                        Plazo detectado por IA · Añadido al calendario
                                    </div>
                                    <div className="font-display text-xl font-black text-ink-950 leading-tight">
                                        {communication.extracted_deadline_label || "Acción requerida"}
                                    </div>
                                    <div className="text-sm text-ink-700 font-bold mt-1">
                                        {communication.extracted_deadline_date}
                                    </div>
                                </div>
                                {communication.extracted_deadline_confidence && (
                                    <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-white text-ink-500 border border-ink-200 shrink-0">
                                        Confianza: {communication.extracted_deadline_confidence}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : communication.extracted_deadline_confidence === "pending" ? (
                        <div className="mb-5 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-3">
                            <Loader2 className="text-amber-600 animate-spin" size={18} />
                            <div className="text-sm font-bold text-amber-700">
                                La IA está leyendo la comunicación para detectar un plazo…
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={async () => {
                                try {
                                    await extractCommunicationDeadline(communication.id);
                                    toast.success("Re-analizado");
                                    await onRefresh();
                                } catch {
                                    toast.error("Error al analizar");
                                }
                            }}
                            className="mb-5 inline-flex items-center gap-1.5 text-xs font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 px-3 py-1.5 rounded-full transition-all"
                            data-testid="comunicaciones-extract-deadline"
                        >
                            <Sparkles size={12} />
                            Detectar plazo con IA
                        </button>
                    )}

                    {communication.body ? (
                        <pre className="text-sm text-ink-700 leading-relaxed whitespace-pre-wrap font-sans">
                            {communication.body}
                        </pre>
                    ) : (
                        <div className="text-sm text-ink-400 italic">Sin contenido escrito.</div>
                    )}

                    <div className="mt-6 pt-5 border-t border-ink-100">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500 flex items-center gap-1.5">
                                <Paperclip size={12} />
                                Adjuntos · {communication.attachments?.length || 0}
                            </div>
                            <Button
                                onClick={() => fileInput.current?.click()}
                                disabled={uploading}
                                variant="outline"
                                className="rounded-full h-9 px-4 border-ink-200 font-bold gap-1.5"
                                data-testid="comunicaciones-attach"
                            >
                                {uploading ? (
                                    <Loader2 size={13} className="animate-spin" />
                                ) : (
                                    <Plus size={13} />
                                )}
                                Subir adjunto
                            </Button>
                            <input
                                ref={fileInput}
                                type="file"
                                className="hidden"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) handleAttach(f);
                                    e.target.value = "";
                                }}
                            />
                        </div>
                        {(communication.attachments || []).length === 0 ? (
                            <div className="text-xs text-ink-400 italic">Sin adjuntos.</div>
                        ) : (
                            <div className="space-y-2">
                                {communication.attachments.map((a) => (
                                    <div
                                        key={a.id}
                                        className="flex items-center gap-3 p-3 bg-ink-50 rounded-xl"
                                        data-testid={`comunicaciones-attach-${a.id}`}
                                    >
                                        <Paperclip size={16} className="text-ink-400 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm text-ink-950 truncate">
                                                {a.filename}
                                            </div>
                                            <div className="text-[10px] font-mono text-ink-400">
                                                {(a.size_bytes / 1024).toFixed(1)} KB · {a.uploaded_at?.slice(0, 16).replace("T", " ")}
                                            </div>
                                        </div>
                                        <a
                                            href={communicationAttachmentUrl(communication.id, a.id)}
                                            download
                                            className="p-2 rounded-md hover:bg-white text-emerald-600"
                                            title="Descargar"
                                        >
                                            <Download size={14} />
                                        </a>
                                        <button
                                            onClick={() => handleDeleteAtt(a.id)}
                                            className="p-2 rounded-md hover:bg-white text-red-500"
                                            title="Eliminar"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}
